import * as angular from 'angular';
import * as _ from 'lodash';

import { Alias, AliasBinding, ComponentDefinition, VariableTableViewModel } from '../../../../models';
import { guidEmpty, Helper } from '../../../../common';
import { IAliasService, ILoggerService } from '../../../../services';

interface Nest {
    key: string;
    values: VariableTableViewModel[];
}

class VariableTypePaneController implements angular.IComponentController {
    formController: angular.IFormController;
    guidEmpty = guidEmpty;
    isEditMode: boolean;
    isExpanded: boolean;
    isLoading: boolean;
    viewModel: Nest;

    private _pristine: Nest;

    static $inject = ['aliasService', 'loggerService'];
    constructor(
        private _aliasService: IAliasService,
        private _loggerService: ILoggerService
    ) {
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['viewModel'] && changesObj['viewModel'].currentValue) {
            this._pristine = angular.copy(changesObj['viewModel'].currentValue);
        }
    }

    toggle() {
        this.isExpanded = !this.isExpanded;
    }

    editTable() {
        this.isEditMode = true;
    }

    save() {
        this.isLoading = true;

        const previous = _.flatten(this._pristine.values.map(d => d.customAliases));
        const currentAliases = _.flatten(this.viewModel.values.map(d => d.customAliases));
        const currentGuids = currentAliases.map(a => a.guid);
        const newAliases = currentAliases.filter(f => !_.some(previous, p => p.guid === f.guid));
        const deletedAliases = previous.filter(p => currentGuids.indexOf(p.guid) < 0).map(d => d.guid);
        const bindingsToUpdate: AliasBinding[] = [];
        const bindingsToDelete: AliasBinding[] = [];
        this.viewModel.values.forEach((row, i) => {
            const oldRow = this._pristine.values[i];
            if (oldRow.reportingAlias !== row.reportingAlias) {
                if (row.reportingAlias === guidEmpty) {
                    bindingsToDelete.push({
                        aliasGuid: oldRow.reportingAlias,
                        guid: oldRow.aliasBindingGuid,
                        unitGuid: oldRow.unitGuid,
                        variableSubTypeGuid: oldRow.subTypeGuid,
                        variableTypeGuid: oldRow.guid
                    });
                } else {
                    const bindingGuid = row.aliasBindingGuid === guidEmpty
                        ? Helper.getGuid()
                        : row.aliasBindingGuid;
                    bindingsToUpdate.push({
                        aliasGuid: row.reportingAlias,
                        guid: bindingGuid,
                        unitGuid: row.unitGuid,
                        variableSubTypeGuid: row.subTypeGuid,
                        variableTypeGuid: row.guid
                    });
                }
            }
        });

        // don't use $q.all to prevent concurrent access
        this._aliasService.add(newAliases)
            .then(() => this._aliasService.deleteItems(deletedAliases))
            .then(() => this._aliasService.updateBindings(bindingsToUpdate, bindingsToDelete))
            .then(() => {
                const updateCount = newAliases.length + bindingsToUpdate.length;
                const deleteCount = deletedAliases.length + bindingsToDelete.length;
                this._loggerService.logSuccess(`${updateCount} aliases created, ${deleteCount} aliases deleted.`);
                this.formController.$setPristine();
                const prisitine = angular.copy(this.viewModel);
                this._pristine = prisitine;
                this.isEditMode = false;
            }).finally(() => {
                this.isLoading = false;
            });
    }

    cancel() {
        this.viewModel = angular.copy(this._pristine);
        this.formController.$setPristine();
        this.isEditMode = false;
    }

    createNew($model: string) {
        return {
            name: $model,
            guid: ''
        };
    }

    onSelect(item: Alias, viewModel: VariableTableViewModel, $index: number) {
        item.variableTypeGuid = viewModel.guid;
        item.variableSubTypeGuid = viewModel.subTypeGuid;
        item.unitGuid = viewModel.unitGuid;
        item.guid = Helper.getGuid();
        const aliases = viewModel.customAliases.concat(viewModel.defaultAliases);
        if (_.some(aliases, c => c.name === item.name && c.guid !== item.guid)) {
            this.formController['alias' + $index].$setValidity('duplicate', false);
        } else {
            this.formController['alias' + $index].$setValidity('duplicate', true);
        }
        viewModel.combinedAliases.push(item);
    }

    onRemove(item: Alias, viewModel: VariableTableViewModel, $index: number) {
        const names = viewModel.customAliases.map(c => c.name);
        const uniques = _.uniq(names);
        this.formController['alias' + $index].$setValidity('duplicate', names.length === uniques.length);
        viewModel.combinedAliases = viewModel.combinedAliases.filter(a => a.guid !== item.guid);
        if (viewModel.reportingAlias === item.guid) {
            viewModel.reportingAlias = guidEmpty;
        }
    }

    aliasName(viewModel: VariableTableViewModel) {
        const found = _.find(viewModel.combinedAliases, ca => ca.guid === viewModel.reportingAlias);
        return found ? found.name : '';
    }
}

export const wtVariableTypePane: ComponentDefinition = {
    componentId: 'wtVariableTypePane',
    controller: VariableTypePaneController,
    template: require('./variableTypePane.component.html'),
    controllerAs: 'vm',
    bindings: {
        viewModel: '<',
        isExpanded: '=',
    }
};
